import * as React from 'react';
import clsx from 'clsx';
import { Avatar } from '@/components/common/Avatar';
import type { SidebarHeaderProps } from './types';
import { getHeaderConfig } from './utils';
import { useAuth } from '@/hooks';
import { getUserFullName, getUserInitials } from '@/utils/formatters';

/**
 * SidebarHeader Component
 * Displays the brand logo and the user's profile summary (Avatar + Stats).
 * Refactored for SoC: visual config in utils, prop types in types.
 */
export const SidebarHeader: React.FC<SidebarHeaderProps> = ({
  isCollapsed,
  echoCount = 0,
  fanCount = 0,
  credits: creditsProp,
}) => {
  const config = React.useMemo(() => getHeaderConfig(isCollapsed), [isCollapsed]);
  const { user } = useAuth();

  const fullName = getUserFullName(user);
  const initials = getUserInitials(user);
  const credits = creditsProp ?? user?.credits ?? 0;

  return (
    <div
      className={clsx(
        'flex w-full flex-col items-center',
        'bg-brand-50',
        'transition-all duration-300',
        'rounded-tr-sidebar',
        config.containerPadding,
        // Force 12px bottom padding, overriding utils config
        '!pb-3',
        // Ensure padding on mobile so the row doesn't touch edges
        !isCollapsed && 'px-6'
      )}
    >
      {/* Logo Section */}
      <div
        className={clsx(
          'mb-[clamp(4px,1dvh,24px)] flex h-[49px] w-full items-center overflow-hidden xl:mb-6',
          'justify-start md:justify-center'
        )}
      >
        <img src={config.logoSrc} alt='d.echo' className={config.logoClass} />
      </div>

      {/* User Profile Section */}
      <div
        className={clsx(
          'flex w-full transition-all duration-300',
          isCollapsed
            ? 'flex-col items-center text-center' // Desktop Collapsed: Stacked
            : clsx(
                // Mobile/Tablet: Row, Left Aligned, Gap
                'flex-row items-center gap-4 text-left',
                // Tablet & Desktop (>= md): Column, Centered, No Gap (margin handled by children)
                'md:flex-col md:items-center md:gap-0 md:text-center'
              )
        )}
      >
        <Avatar
          className={clsx(
            config.avatarClass,
            // Reset margins inside the layout wrapper to handle spacing via gap/flex
            '!mb-0',
            isCollapsed
              ? '!mb-2 !h-[65px] !w-[65px]' // Collapsed: 65px + bottom margin
              : clsx(
                  '!h-[clamp(48px,9dvh,72px)] !w-[clamp(48px,9dvh,72px)]', // Phone: scales 48-72px
                  'md:!mb-3 md:!h-[clamp(56px,10dvh,90px)] md:!w-[clamp(56px,10dvh,90px)]', // Tablet: scales 56-90px
                  'xl:!h-[90px] xl:!w-[90px]' // Desktop: locked 90px
                )
          )}
          size={isCollapsed ? 65 : 90}
          initials={initials}
          src={user?.profile_picture_url ?? null}
          textClassName='font-proxima font-bold text-[25px] leading-[20px] tracking-[0%] uppercase text-brand-800'
        />

        {/* Content (Stats / Name) */}
        {isCollapsed ? (
          <div className='animate-slide-in flex flex-col items-center'>
            <div className='whitespace-nowrap text-14 font-medium leading-27 text-dark-100'>
              <span>{echoCount}/1</span> <span className='text-gray-500'>echoes</span>
            </div>
            <div className='whitespace-nowrap text-14 font-medium leading-27 text-dark-100'>
              <span>{fanCount}</span> <span className='text-gray-500'>fans</span>
            </div>
            <div className='whitespace-nowrap text-14 font-medium leading-27 text-dark-100'>
              <span>{credits}</span> <span className='text-gray-500'>credits</span>
            </div>
          </div>
        ) : (
          <div className='animate-slide-in flex flex-col justify-center'>
            <h3 className='whitespace-nowrap text-base font-bold leading-27 text-dark-100'>
              {fullName}
            </h3>

            <div className='mt-1 whitespace-nowrap text-[15px] font-medium leading-27 text-dark-100'>
              <span>{echoCount}/1</span> <span className='text-gray-500'>echoes created</span>
            </div>

            <div
              className={clsx(
                'flex whitespace-nowrap text-[15px] font-medium leading-27 text-dark-100',
                'justify-start',
                'md:items-center md:justify-center md:gap-2'
              )}
            >
              <span className='mr-2 md:mr-0'>
                {fanCount} <span className='text-gray-500'>fans</span>
              </span>

              <span className='hidden h-3 w-px bg-gray-300 md:block' />

              <span>
                {credits} <span className='text-gray-500'>credits</span>
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SidebarHeader;
